import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatErr } from "../../../../../lib/api";
import { Button } from "../../../../../components/ui/button";
import { Input } from "../../../../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../../components/ui/table";
import { toast } from "sonner";
import { EmptyState, Field, SectionCard } from "../shared";
import { ACCOUNTING_BASE } from "../accountingNav";
import { formatCurrency, formatDateDMY, downloadReportCsv } from "./reportShared";

/**
 * Phase 15 (Vendor Bills & Payables) items 5 + 9: per-Vendor-Bill
 * Outstanding list - Bill No./Bill Date/Due Date/Original Amount/Paid/
 * Balance/Age, filterable by Vendor, Bill No., Date range and Outstanding/
 * Paid status, with a free-text Search box, backed by the new
 * GET /society/accounting/reports/vendor-bills endpoint (paid/balance/age
 * computed live from each bill's paymentAllocations - never a cached
 * figure). Actions are View | Edit | Print | Delete, each routed to the
 * existing generic engine voucher endpoints - Delete surfaces the exact
 * backend dependency reason (item 7) via formatErr/toast when a payment is
 * linked, unchanged from every other engine voucher Delete action in this
 * module.
 */
export default function VendorBillsOutstandingPage() {
  const navigate = useNavigate();
  const [vendorId, setVendorId] = useState("all");
  const [billNo, setBillNo] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [vendors, setVendors] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = {
    vendorId: vendorId === "all" ? "" : vendorId,
    billNo, fromDate, toDate,
    status: status === "all" ? "" : status,
    q,
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get("/society/accounting/reports/vendor-bills", { params });
      setData(res.data);
    } catch (err) {
      toast.error(formatErr(err, "Could not load vendor bills"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [vendorId, billNo, fromDate, toDate, status]);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/society/accounting/engine/vendors");
        setVendors(Array.isArray(res.data) ? res.data : []);
      } catch {
        setVendors([]);
      }
    })();
  }, []);

  const rows = useMemo(() => {
    const list = data?.rows || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((r) => `${r.vendorName} ${r.billNo} ${r.narration}`.toLowerCase().includes(needle));
  }, [data, q]);

  const deleteBill = async (row) => {
    if (!window.confirm(`Delete Vendor Bill ${row.billNo}? This cannot be undone.`)) return;
    try {
      await api.delete(`/society/accounting/engine/vouchers/${row.billVoucherId}`);
      toast.success("Vendor Bill deleted");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not delete this Vendor Bill"));
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Vendor Bills — Outstanding</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Every posted Vendor Bill with its Paid/Balance/Age, computed live from allocated payments.</p>
      </div>

      <SectionCard title="Filters">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Vendor">
            <Select value={vendorId} onValueChange={setVendorId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All vendors</SelectItem>
                {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.vendorName}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Bill No.">
            <Input value={billNo} onChange={(e) => setBillNo(e.target.value)} placeholder="e.g. INV-1024" />
          </Field>
          <Field label="From Date"><Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} /></Field>
          <Field label="To Date"><Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} /></Field>
          <Field label="Status">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="outstanding">Outstanding</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Search">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Vendor, bill no. or narration" />
          </Field>
        </div>
        <div className="mt-3 flex gap-2">
          <Button size="sm" variant="outline" onClick={() => downloadReportCsv("/society/accounting/reports/vendor-bills", params, `vendor-bills-${new Date().toISOString().slice(0, 10)}.csv`)}>Export CSV</Button>
          <Button size="sm" variant="outline" onClick={() => { setVendorId("all"); setBillNo(""); setFromDate(""); setToDate(""); setStatus("all"); setQ(""); }}>Reset</Button>
        </div>
      </SectionCard>

      <div className="overflow-x-auto rounded-sm border border-[#E5E7EB] bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Bill No.</TableHead><TableHead>Bill Date</TableHead><TableHead>Due Date</TableHead><TableHead>Vendor</TableHead>
              <TableHead className="text-right">Original</TableHead><TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Balance</TableHead><TableHead className="text-right">Age</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="py-8 text-center text-[#6B7280]">Loading...</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="py-0"><div className="py-8"><EmptyState>No vendor bills match this filter.</EmptyState></div></TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.billVoucherId}>
                <TableCell className="font-mono text-xs font-semibold">{r.billNo}</TableCell>
                <TableCell>{formatDateDMY(r.billDate)}</TableCell>
                <TableCell>{r.dueDate ? formatDateDMY(r.dueDate) : "-"}</TableCell>
                <TableCell>{r.vendorName || "-"}</TableCell>
                <TableCell className="text-right">{formatCurrency(r.originalAmount)}</TableCell>
                <TableCell className="text-right">{formatCurrency(r.paid)}</TableCell>
                <TableCell className="text-right font-semibold">{formatCurrency(r.balance)}</TableCell>
                <TableCell className="text-right">{r.age !== null && r.age !== undefined ? `${r.age}d` : "-"}</TableCell>
                <TableCell>
                  <span className={`rounded-sm px-2 py-0.5 text-xs font-semibold ${r.status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    {r.status === "paid" ? "Paid" : "Outstanding"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex flex-wrap justify-end gap-2 text-xs font-semibold">
                    <Link to={`${ACCOUNTING_BASE}/vouchers/${r.billVoucherId}`} className="text-[#111827] hover:underline">View</Link>
                    <Link to={`${ACCOUNTING_BASE}/vouchers/${r.billVoucherId}/edit`} className="text-[#111827] hover:underline">Edit</Link>
                    <Link to={`${ACCOUNTING_BASE}/vouchers/${r.billVoucherId}?print=1`} className="text-[#111827] hover:underline">Print</Link>
                    <button type="button" onClick={() => deleteBill(r)} className="text-red-600 hover:underline">Delete</button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
